import { skipToken } from '@apollo/client/react'
import {
  Button,
  DialogActions,
  FormControl,
  IconButton,
  Input,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Slider,
  Tooltip,
} from '@mui/material'
import { clamp } from 'lodash-es'
import type { FC } from 'react'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { match } from 'ts-pattern'
import IcBaselineClose from '~icons/ic/baseline-close'
import IcBaselineDeleteForever from '~icons/ic/baseline-delete-forever'
import IcBaselineEdit from '~icons/ic/baseline-edit'
import IcBaselineFileUpload from '~icons/ic/baseline-file-upload'
import IcBaselineMenuBook from '~icons/ic/baseline-menu-book'
import Empty from 'src/components/Empty'
import ModelSelectDialog from 'src/components/GenerationModel/ModelSelectDialog'
import type { TaskSubmitFormValues } from 'src/components/GenerationTask/BaseTaskSubmitForm'
import { useTaskSubmitFormContext } from 'src/components/GenerationTask/BaseTaskSubmitForm'
import type { SizeInfo } from 'src/components/GenerationTask/constants'
import { PRESET_SIZES } from 'src/components/GenerationTask/constants'
import SingleImageViewer from 'src/components/Media/SingleImageViewer'
import { Preloader } from 'src/components/Preloader/Preloader'
import { UIButton } from 'src/components/ui/Button'
import {
  UIDialog,
  UIDialogContent,
  UIDialogHeader,
} from 'src/components/ui/Modal'
import type { Media } from 'src/services/apollo/generated/graphql'
import { UserSettingType } from 'src/services/apollo/generated/graphql'
import { useGetGenerationModelByVersionId } from 'src/services/apollo/generation-model'
import { fetchGetMedia } from 'src/services/apollo/media'
import {
  executeDeleteMySetting,
  executeUpsertMySetting,
  useGetMySetting,
  useGetMySettingLimit,
  useListMySettings,
} from 'src/services/apollo/setting'
import {
  useDynamicConstants,
  useDynamicDefaultParameters,
} from 'src/services/constants'
import { createCtx } from 'src/utils/contextState'
import { cx } from 'src/utils/cx'
import { getErrorData } from 'src/utils/errorUtil'
import { getNodesFromConnection } from 'src/utils/graphql'
import { useObjectUrl } from 'src/utils/hooks/useObjectUrl'
import { getAspectRatio } from 'src/utils/media'
import { track } from 'src/utils/tracker'
import { UnFile } from 'src/utils/unFile'
import useLoadingCallback from 'src/utils/useLoadingCallback'
import { CurrentModelContent } from '../../Common/ModelSelect'
import { SizeInput } from '../../controls/ImageSizeControl'
import { parseInitialValuesForNote } from '../../utils'

export type NoteContentData = {
  title?: string
  prompts?: string
  negativePrompts?: string
  modelId?: string
  samplingMethod?: string
  samplingSteps?: number
  cfgScale?: number
  seed?: string
  baseImageMediaId?: string
  width?: number
  height?: number
}

type NoteContent = {
  id: string
  data: NoteContentData
}

const TaskNotes: typeof TaskNotesContent = function (props) {
  return (
    <OpenListDialogContextProvider>
      <TaskNotesContent {...props} />
    </OpenListDialogContextProvider>
  )
}
export default TaskNotes

type TaskNotesContentProps = {
  iconOnly?: boolean
  iconProps?: StyleProps
} & StyleProps

const TaskNotesContent: FC<TaskNotesContentProps> = function ({
  iconOnly = false,
  className,
  style,
  iconProps = {},
}) {
  const { t } = useTranslation(['artwork'])
  const [{ open }, setOpenDialog] = useContext(ListDialogContext)

  return (
    <>
      <button
        className={className}
        style={style}
        onClick={() => {
          track('notes_click')
          setOpenDialog({ open: true })
        }}
      >
        <IcBaselineMenuBook {...iconProps} />
        <span className={cx({ hidden: iconOnly })}>
          {t('artwork:note.notes')}
        </span>
      </button>

      <TaskNotesListDialog
        open={open}
        onClose={() => setOpenDialog({ open: false })}
      />
    </>
  )
}

function TaskNotesListDialog(props: { open: boolean; onClose: () => any }) {
  const {
    data: settingsData,
    loading,
    refetch: refetchList,
  } = useListMySettings({
    type: UserSettingType.Note,
    last: 10,
  })
  const { data: limit } = useGetMySettingLimit(UserSettingType.Note)
  const { t } = useTranslation(['artwork'])
  const notes = useMemo(
    () => getNodesFromConnection(settingsData).reverse(),
    [settingsData],
  )
  const notesCount = settingsData?.totalCount ?? 0

  const noteLimit = limit ?? 0
  const limitReached = noteLimit <= notesCount

  const ListContent =
    notes.length < 1 ? (
      loading ? (
        <Preloader />
      ) : (
        <div className="w-full flex flex-col gap-2 items-center justify-center py-12">
          <Empty />
          <p>{t('artwork:note.saved-notes.no-notes')}</p>
        </div>
      )
    ) : (
      <div className="w-full px-4 flex flex-col gap-1 min-w-[400px] overflow-y-auto">
        {notes.map(note => (
          <NoteContentPreview note={note} key={note.id} />
        ))}
      </div>
    )

  return (
    <>
      <UpsertNoteDialogContextProvider>
        <UIDialog
          open={props.open}
          onClose={props.onClose}
          className="max-w-[100vw-64px] min-w-[352px]"
          dismissable
        >
          <UIDialogHeader
            className="relative"
            heading={
              <>
                <p className="font-semibold text-xl">
                  {t('artwork:note.saved-notes.title')}
                </p>
                <p className="text-xs">
                  {t('artwork:note.saved-notes.description')}
                </p>
                <IconButton
                  aria-label="close"
                  onClick={props.onClose}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                  }}
                >
                  <IcBaselineClose />
                </IconButton>
              </>
            }
          />
          {ListContent}
          <div className="w-full flex p-4 gap-4 justify-between">
            <ActionButtons limitReached={limitReached} />
          </div>
        </UIDialog>
        <UpsertNoteDialogWrapper refreshList={refetchList} />
      </UpsertNoteDialogContextProvider>
    </>
  )
}

function ActionButtons(props: { limitReached?: boolean }) {
  const { t } = useTranslation(['artwork'])
  const [, setUpsertDialog] = useContext(UpsertNoteDialogContext)

  return props.limitReached ? (
    <>
      <Tooltip
        title={t('artwork:note.notice.max-limit-reached')}
        placement="top"
      >
        <Button
          className="font-bold cursor-not-allowed opacity-20"
          variant="outlined"
          disableRipple
        >
          {t('artwork:note.add-new-note')}
        </Button>
      </Tooltip>
      <Tooltip
        title={t('artwork:note.notice.max-limit-reached')}
        placement="top"
      >
        <Button
          className="font-bold cursor-not-allowed opacity-20"
          variant="contained"
          disableRipple
        >
          {t('artwork:note.save-current-params')}
        </Button>
      </Tooltip>
    </>
  ) : (
    <>
      <Button
        className="font-bold"
        variant="outlined"
        onClick={() => {
          track('add_note_click')
          setUpsertDialog({
            open: true,
            openFrom: 'new',
          })
        }}
      >
        {t('artwork:note.add-new-note')}
      </Button>
      <Button
        className="font-bold"
        variant="contained"
        onClick={() => {
          track('save_current_click')
          setUpsertDialog({
            open: true,
            loadFromTaskForm: true,
            openFrom: 'generate',
          })
        }}
      >
        {t('artwork:note.save-current-params')}
      </Button>
    </>
  )
}

function NoteContentPreview(props: { note: NoteContent }) {
  const { t } = useTranslation(['artwork', 'user', 'common', 'action'])
  const title = props.note.data.title || '(Empty Title)'
  const prompts = props.note.data.prompts || '(Empty Prompts)'
  const [, setUpsertDialog] = useContext(UpsertNoteDialogContext)
  const [, setListDialog] = useContext(ListDialogContext)
  const { data: defaultParamsConfig } = useDynamicDefaultParameters()
  const { setValues } = useTaskSubmitFormContext()
  const { data: { samplingMethods } = {} } = useDynamicConstants()

  const onClick = async () => {
    const toProceed = await UIDialog.confirm({
      heading: t('artwork:note.apply-note.title'),
      content: t('artwork:note.apply-note.desc'),
    })
    if (!toProceed) return

    const { baseImageMediaId } = props.note.data
    const media = baseImageMediaId
      ? (await fetchGetMedia({ id: baseImageMediaId })).data?.media
      : undefined

    setValues({
      ...defaultParamsConfig,
      ...parseInitialValuesForNote({
        media,
        noteContent: props.note.data,
        samplingMethods,
        extraInitialValues: {
          seed: props.note.data.seed ?? '', // explicitly overwrite seed, even if it's empty
        },
      }),
    })
    track('prompt_notes_selected')
    setListDialog({ open: false })
  }

  return (
    <div
      role="button"
      className="flex flex-col items-start rounded-xl hover:bg-neutral-200 px-4 py-2 transition-all group"
      onClick={onClick}
    >
      <h5 className="font-semibold">{title}</h5>
      <div className="inline-flex w-full items-center">
        <p className="text-neutral-400 font-light line-clamp-2 max-w-[400px]">
          {prompts}
        </p>
        <IconButton
          className="shrink-0 w-12 h-12 ml-auto"
          onClick={e => {
            e.stopPropagation()
            track('edit_note_click')
            setUpsertDialog({
              open: true,
              noteId: props.note.id,
              openFrom: 'edit',
            })
          }}
        >
          <IcBaselineEdit className="size-5" />
        </IconButton>
      </div>
    </div>
  )
}

const convertTaskValuesToNote = ({
  width,
  height,
  baseImage,
  prompts,
  negativePrompts,
  modelId,
  samplingMethod,
  samplingSteps,
  cfgScale,
  seed,
}: TaskSubmitFormValues): UpsertNotePrefilledData => ({
  width,
  height,
  baseImageMedia: baseImage?.ifIs('media', m => m),
  baseImageFile: baseImage?.ifIs('file', f => f),
  prompts,
  negativePrompts,
  modelId,
  samplingMethod,
  samplingSteps,
  cfgScale,
  seed,
})

function UpsertNoteDialogWrapper(props: { refreshList: () => any }) {
  const [{ noteId, open, loadFromTaskForm }, setUpsertDialog] = useContext(
    UpsertNoteDialogContext,
  )
  const { values } = useTaskSubmitFormContext()

  const hasLoaded = useRef(false)
  const { data: note, loading } = useGetMySetting(
    noteId && !loadFromTaskForm ? noteId : skipToken,
  )

  // ensure only do initial fetch on every dialog open
  useEffect(() => {
    if (hasLoaded.current) return
    if (!loading && !!note) {
      hasLoaded.current = true
    }

    return () => {
      hasLoaded.current = false
    }
  }, [loading, note, hasLoaded])

  const prefilledData = loadFromTaskForm
    ? convertTaskValuesToNote(values)
    : note?.data

  return (
    <UpsertNoteDialog
      open={open}
      noteId={noteId}
      onClose={() => setUpsertDialog({ open: false })}
      refreshList={props.refreshList}
      prefilledData={prefilledData}
      isLoading={!hasLoaded.current && loading}
    />
  )
}

export function UpsertNoteDialog(props: {
  open: boolean
  onClose: () => any
  noteId?: string
  refreshList?: () => any
  prefilledData?: UpsertNotePrefilledData
  isLoading?: boolean
  openFrom?: 'new' | 'generate' | 'edit' | 'image'
}) {
  const [ctxState] = useContext(UpsertNoteDialogContext)
  const { t } = useTranslation(['artwork'])
  const { open, noteId, isLoading } = props
  const { baseImageMedia, baseImageFile, ...restPrefilledData } =
    props.prefilledData ?? {}

  const onClose = () => {
    props.refreshList?.()
    props.onClose()
  }

  // props.openFrom takes precedence
  const openFrom = props.openFrom ?? ctxState.openFrom

  return (
    <UIDialog
      open={open}
      onClose={onClose}
      className="max-w-[600px] max-h-[calc(100%-64px)]"
      dismissable
    >
      <UIDialogHeader
        className="m-0 p-4 relative"
        heading={
          <>
            <p className="font-semibold text-xl">
              {noteId
                ? t('artwork:note.edit-note')
                : t('artwork:note.new-note')}
            </p>
            <IconButton
              aria-label="close"
              onClick={onClose}
              className="absolute right-1 top-1 msm:right-3 msm:top-3"
            >
              <IcBaselineClose />
            </IconButton>
          </>
        }
      ></UIDialogHeader>
      <UIDialogContent className="min-w-[400px]">
        {isLoading ? (
          <Preloader />
        ) : (
          <EditFormStateContextProvider
            defaultValue={props.prefilledData ? restPrefilledData : undefined}
          >
            <BaseImageInitializer media={baseImageMedia} file={baseImageFile} />
            <NewNoteForm />
            <DialogActions className="justify-between">
              <NoteActions
                noteId={noteId}
                onSuccess={onClose}
                openFrom={openFrom}
              />
            </DialogActions>
          </EditFormStateContextProvider>
        )}
      </UIDialogContent>
    </UIDialog>
  )
}

function NoteActions(props: {
  noteId?: string
  onSuccess?: () => any
  /**
   * For data tracking only
   */
  openFrom?: 'new' | 'generate' | 'edit' | 'image'
}) {
  const { t } = useTranslation(['action', 'generation'])
  const [formState, setFormState] = useContext(EditFormStateContext)

  const [saving, onSave] = useLoadingCallback(async () => {
    try {
      const baseImageMedia =
        formState.baseImage &&
        (await toast.promise(
          formState.baseImage.toMedia({
            onCompressed(baseImage) {
              setFormState(prev => ({ ...prev, baseImage }))
            },
          }),
          { pending: `${t('generation:submit.notice.uploading-image')}` },
        ))

      const res = await executeUpsertMySetting({
        id: props.noteId,
        type: UserSettingType.Note,
        data: {
          ...formState,
          baseImage: undefined,
          size: undefined,
          baseImageMediaId: baseImageMedia?.id,
        } as NoteContentData,
      })
      track('save_note_click', {
        page: props.openFrom,
      })
      toast.success(
        props.noteId ? 'Successfully updated' : 'Successfully created',
      )
      props.onSuccess?.()
    } catch (e: any) {
      const errMessage = match(getErrorData(e)?.code)
        // TODO: Reuse error codes from backend
        .with(403e5 + 35, () => t('artwork:note.notice.max-limit-reached'))
        .otherwise(() => e.message)
      toast.error(
        t('artwork:note.notice.failed-to-save') +
          t('common:punctuations.colon') +
          ' ' +
          errMessage,
      )
    }
  }, [formState])

  const [deleting, onDelete] = useLoadingCallback(async () => {
    if (!props.noteId) return
    try {
      const res = await executeDeleteMySetting({
        id: props.noteId,
      })
      toast.success('Successfully deleted')
      track('delete_note_click')
      props.onSuccess?.()
    } catch (e: any) {
      toast.error(e.message)
    }
  }, [formState])

  const loading = saving || deleting

  return (
    <>
      {props.noteId && (
        <UIButton
          color="danger"
          busy={loading}
          disabled={loading}
          onPress={onDelete}
          icon={<IcBaselineDeleteForever className="size-5" />}
        >
          {t('action:delete.label')}
        </UIButton>
      )}
      <UIButton
        busy={loading}
        disabled={loading}
        onPress={onSave}
        variant="filled"
        className="ml-auto"
      >
        {t('action:save')}
      </UIButton>
    </>
  )
}

function NewNoteForm() {
  const { t } = useTranslation(['artwork', 'generation'])
  const [formState, setFormState] = useContext(EditFormStateContext)

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof typeof formState,
  ) => {
    setFormState(prev => ({
      ...prev,
      [field]: e.target.value,
    }))
  }

  return (
    <form
      noValidate
      autoComplete="off"
      onSubmit={e => {
        e.preventDefault()
      }}
      className="py-4 flex flex-col gap-4"
    >
      <FormControl fullWidth>
        <InputLabel htmlFor="title-input">{t('artwork:title')}</InputLabel>
        <OutlinedInput
          value={formState.title}
          onChange={e => onChange(e, 'title')}
          id="title-input"
          label={t('artwork:title')}
        />
      </FormControl>
      <FormControl fullWidth>
        <InputLabel htmlFor="prompts-input">
          {t('artwork:prompts.prompts')}
        </InputLabel>
        <OutlinedInput
          placeholder="Enter prompts"
          id="prompts-input"
          value={formState.prompts}
          onChange={e => onChange(e, 'prompts')}
          label={t('artwork:prompts.prompts')}
        />
      </FormControl>
      <FormControl fullWidth>
        <InputLabel htmlFor="negative-prompts-input">
          {t('generation:negative-prompts.label')}
        </InputLabel>
        <OutlinedInput
          placeholder="Enter negative prompts"
          id="negative-prompts-input"
          value={formState.negativePrompts}
          onChange={e => onChange(e, 'negativePrompts')}
          label={t('generation:negative-prompts.label')}
        />
      </FormControl>
      <div className="grid grid-cols-2 gap-y-4 gap-x-8 items-center">
        <ModelSelectMenu />
        <SizeSelectMenu />
        <AdvancedParamsFormItems />
      </div>
      <div className="w-[50%]">
        <BaseMediaSelectBtn />
      </div>
    </form>
  )
}

function ModelSelectMenu() {
  const [openDialog, setOpenDialog] = useState(false)
  const { t } = useTranslation(['artwork', 'generation'])
  const [formState, setFormState] = useContext(EditFormStateContext)
  const { modelId } = formState

  const { data: modelVersion, loading: versionLoading } =
    useGetGenerationModelByVersionId(modelId || skipToken)

  const dialogState = {
    type: null,
    versionId: modelVersion?.id || null,
    modelId: modelVersion?.modelId || null,
    searchKeyword: '',
    searchKeywordForInput: '',
    openDetail: false,
    tab: 'preset' as const,
  }

  return (
    <div className="max-w-[500px]">
      <label className="text-stone-500 text-sm mb-1">
        {t('artwork:note.base-model.label')}
      </label>
      <button
        onClick={e => {
          if (e.target instanceof HTMLAnchorElement) return
          setOpenDialog(true)
        }}
        className="w-full text-start"
      >
        {modelId ? (
          <CurrentModelContent
            modelVersion={modelVersion}
            modelVersionId={modelId}
            loading={versionLoading}
          />
        ) : (
          <div className="px-4 py-4 bg-background-light rounded-xl">
            <span className="text-sm italic">
              {t('generation:model.select.model.placeholder')}
            </span>
          </div>
        )}
      </button>
      <ModelSelectDialog
        onClose={() => setOpenDialog(false)}
        open={openDialog}
        initState={dialogState}
        onVersionSelected={version => {
          setFormState(prev => ({ ...prev, modelId: version.id }))
        }}
      />
    </div>
  )
}

function SizeSelectMenu() {
  const { t } = useTranslation(['artwork'])
  const [{ width, height }, setFormState] = useContext(EditFormStateContext)

  const applySize = (sz: SizeInfo) => {
    setFormState(prev => ({ ...prev, width: sz.width, height: sz.height }))
  }

  return (
    <div className="flex items-center gap-2">
      <SizeInput
        value={width}
        onChange={value => setFormState(prev => ({ ...prev, width: value }))}
        className="flex-1"
        label={t('generation:size.width')}
      />
      <span>×</span>
      <SizeInput
        value={height}
        onChange={value => setFormState(prev => ({ ...prev, height: value }))}
        className="flex-1"
        label={t('generation:size.height')}
      />
      <Select
        value=""
        displayEmpty
        renderValue={() => t('generation:size.preset-menu.label')}
        size="small"
        className={cx('text-center flex-none')}
      >
        {PRESET_SIZES.map(sz => {
          const longest = Math.max(sz.width, sz.height)
          return (
            <MenuItem
              key={`${sz.width}-${sz.height}`}
              className="gap-2"
              onClick={() => applySize(sz)}
            >
              <div className="flex-none w-5 h-5 flex justify-center items-center">
                <div
                  className="bg-zinc-300 dark:bg-zinc-500 rounded-sm"
                  style={{
                    width: (sz.width / longest) * 100 + '%',
                    height: (sz.height / longest) * 100 + '%',
                  }}
                />
              </div>
              {sz.width}×{sz.height}
            </MenuItem>
          )
        })}
      </Select>
    </div>
  )
}

function AdvancedParamsFormItems() {
  const { t } = useTranslation(['generation', 'common'])
  const [formState, setFormState] = useContext(EditFormStateContext)
  const {
    data: { samplingMethods = [] } = {},
    isValidating: dynamicConstantsLoading,
  } = useDynamicConstants()

  return (
    <>
      <FormControl>
        <label
          htmlFor="sampling-steps-select"
          className="text-sm text-stone-500"
        >
          {t('generation:sampling-steps.label')}
        </label>

        <div className="inline-flex gap-4">
          <Slider
            id="sampling-steps-select"
            value={formState.samplingSteps ?? 1}
            onChange={(_, value) => {
              if (typeof value === 'number') {
                setFormState(prev => ({
                  ...prev,
                  samplingSteps: value,
                }))
              }
            }}
            min={1}
            max={50}
            step={1}
            valueLabelDisplay="auto"
          />
          <Input
            type="number"
            className="w-[60px]"
            value={formState.samplingSteps ?? 1}
            onChange={e => {
              const value = Number(e.target.value)
              setFormState(prev => ({
                ...prev,
                samplingSteps: clamp(value, 1, 50),
              }))
            }}
            inputProps={{
              min: 1,
              max: 50,
            }}
          />
        </div>
      </FormControl>

      <FormControl>
        <InputLabel id="sampling-method-select">
          {t('generation:sampling-method.label')}
        </InputLabel>

        <Select
          className="w-[200px]"
          labelId="sampling-method-select"
          value={formState.samplingMethod ?? ''}
          onChange={e => {
            setFormState(prev => ({
              ...prev,
              samplingMethod: e.target.value,
            }))
          }}
          label={t('generation:sampling-method.label')}
        >
          <MenuItem value="">
            <em>{t('common:none')}</em>
          </MenuItem>
          {samplingMethods.map(key => {
            return (
              <MenuItem key={key} value={key}>
                {key}
              </MenuItem>
            )
          })}
        </Select>
      </FormControl>

      <FormControl>
        <label htmlFor="cfg-scale-input" className="text-sm text-stone-500">
          {t('generation:cfg-scale.label')}
        </label>

        <div className="inline-flex gap-4">
          <Slider
            id="cfg-scale-input"
            value={formState.cfgScale ?? 1}
            onChange={(_, value) => {
              if (typeof value === 'number') {
                setFormState(prev => ({
                  ...prev,
                  cfgScale: value,
                }))
              }
            }}
            min={1.1}
            max={50}
            step={0.1}
            valueLabelDisplay="auto"
          />
          <Input
            type="number"
            className="w-[60px]"
            value={formState.cfgScale ?? 1}
            onChange={e => {
              const value = Number(e.target.value)
              setFormState(prev => ({
                ...prev,
                cfgScale: clamp(value, 1, 50),
              }))
            }}
            inputProps={{
              min: 1,
              max: 50,
              step: 0.5,
            }}
          />
        </div>
      </FormControl>

      <FormControl>
        <InputLabel htmlFor="seed-input">
          {t('generation:seed.label')}
        </InputLabel>
        <OutlinedInput
          id="seed-input"
          type="number"
          className="max-w-[500px]"
          label={t('generation:seed.label')}
          placeholder={t('generation:seed.placeholder')!}
          value={formState.seed}
          onChange={e => {
            setFormState(prev => ({
              ...prev,
              seed: e.target.value,
            }))
          }}
        />
      </FormControl>
    </>
  )
}

function BaseMediaSelectBtn() {
  const { t } = useTranslation(['generation'])
  const [imageDimension, setImageDimension] = useState<{
    width: number
    height: number
  }>()
  const [{ baseImage }, setFormState] = useContext(EditFormStateContext)
  const imageUrl = useObjectUrl(baseImage)

  useEffect(() => {
    baseImage
      ?.getDimensions()
      .then(setImageDimension)
      .catch(e => console.warn(e))
  }, [baseImage])

  const onRemoveImage = () => {
    setFormState(prev => ({
      ...prev,
      baseImage: undefined,
    }))
  }

  return (
    <>
      <h2 className="text-sm text-stone-500">
        {t('generation:base-image.label')}
      </h2>
      <div className="w-[200px] h-[200px] rounded-xl border border-neutral-300">
        {imageUrl ? (
          <div
            className="flex relative w-full h-full"
            style={{ aspectRatio: getAspectRatio(imageDimension) }}
          >
            <SingleImageViewer
              originalUrl={imageUrl}
              fileName={baseImage!.name}
              imgClassName="flex object-contain w-full h-full rounded-xl !object-center"
              width={imageDimension?.width}
              height={imageDimension?.height}
            />
            <IconButton
              onClick={onRemoveImage}
              className="rounded-full bg-white shadow-sm absolute top-0 right-0 translate-x-3 -translate-y-3"
            >
              <IcBaselineClose className="size-[14px]" />
            </IconButton>
          </div>
        ) : (
          <>
            <Button
              component="label"
              className={cx('font-semibold flex gap-1 border w-full h-full')}
              htmlFor="note-image-upload"
            >
              <IcBaselineFileUpload className="size-6" />
            </Button>
            <input
              id="note-image-upload"
              type="file"
              accept="image/*"
              hidden
              onClick={e => {
                e.currentTarget.value = ''
              }}
              onChange={e => {
                const [file] = e.target.files || []
                if (!file) return
                setFormState(prev => ({
                  ...prev,
                  baseImage: UnFile.fromFile(file),
                }))
              }}
            />
          </>
        )}
      </div>
    </>
  )
}

type EditFormState = Omit<NoteContentData, 'baseImageMediaId'> & {
  baseImage?: UnFile
  size?: number
}

type UpsertNotePrefilledData = Omit<EditFormState, 'baseImage'> & {
  baseImageMedia?: Media
  baseImageFile?: File
}

function BaseImageInitializer({ media, file }: { media?: Media; file?: File }) {
  const [, setFormState] = useContext(EditFormStateContext)
  useEffect(() => {
    const baseImage = media
      ? UnFile.fromMedia(media)
      : file
        ? UnFile.fromFile(file)
        : undefined
    if (baseImage) setFormState(prev => ({ ...prev, baseImage }))
  }, [])
  return null
}

const [EditFormStateContext, EditFormStateContextProvider] =
  createCtx<EditFormState>({
    width: 512,
    height: 512,
  })
EditFormStateContext.displayName = 'EditFormStateContext'

const [UpsertNoteDialogContext, UpsertNoteDialogContextProvider] = createCtx<{
  open: boolean
  noteId?: string | undefined
  loadFromTaskForm?: boolean
  openFrom?: 'new' | 'generate' | 'edit' | 'image'
}>({ open: false })
UpsertNoteDialogContext.displayName = 'UpsertNoteDialogContext'

const [ListDialogContext, OpenListDialogContextProvider] = createCtx<{
  open: boolean
}>({ open: false })
